/**
 * Sidebar — unified across admin and publisher shells.
 * Scholarly Modernist aesthetic: Libre Bodoni brand, bell notification, rounded corners.
 * Fixed 240px width. Always expanded.
 *
 * Props:
 * - `userName` — user display name
 * - `userEmail` — user email address
 * - `navSections` — array of { label, items[] } for dynamic nav
 * - `onSignOut` — callback when sign out is clicked
 * - `showBell` — show the notification bell + center popover
 * - `unreadNotificationCount` — unread notification count badge
 *
 * Uses design tokens: --sidebar-*, accent-gold, shadow-warm.
 * No hardcoded colors. RTL-aware via logical properties.
 */
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";
import { Logo } from "@/shared/components/Logo";
import { getEffectiveDir } from "@/shared/lib/direction";
import { UserAvatar } from "@/shared/components/UserAvatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  ChevronDown,
  LogOut,
  Settings,
  UserCircle,
  type LucideIcon,
} from "lucide-react";
import { NotificationCenter } from "@/features/notifications/components/NotificationCenter";

const SIDEBAR_WIDTH = "15rem"; // 240px

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface NavItem {
  href: string;
  icon: LucideIcon;
  labelKey: string; // i18n key
  badge?: string;
  /** Require exact pathname match — prevents prefix collision (e.g. /dashboard vs /dashboard/settings). */
  exact?: boolean;
}

interface NavSection {
  label: string; // i18n key — rendered as uppercase section header
  items: NavItem[];
}

/* ── Props ─────────────────────────────────────────────────────────────────── */

interface SidebarProps {
  userName: string;
  userEmail: string;
  userPhotoUrl?: string | null;
  navSections: NavSection[];
  onSignOut: () => void;
  /** Show the notification bell + center popover. Default: false. */
  showBell?: boolean;
  unreadNotificationCount?: number;
  /** Whether the authenticated user is an admin. Determines settings nav link. */
  isAdmin?: boolean;
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

/** Single nav row */
function NavRow({
  item,
  isActive,
}: {
  item: NavItem;
  isActive: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Link
      to={item.href}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors",
        isActive
          ? "bg-[var(--sidebar-active-bg)] text-[var(--sidebar-text-active)] font-semibold"
          : "text-[var(--sidebar-text-muted)] hover:text-[var(--sidebar-text)] hover:bg-[var(--sidebar-active-bg)]",
      )}
    >
      <span
        className={cn(
          "w-4 h-4 shrink-0 flex items-center",
          isActive ? "text-[var(--sidebar-text-active)]" : "text-[var(--sidebar-text-muted)]",
        )}
      >
        <item.icon className="w-4 h-4" />
      </span>
      <span className="flex-1">{t(item.labelKey)}</span>
      {item.badge && (
        <span
          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
          style={{ background: "var(--sidebar-text-active)" }}
        >
          {item.badge}
        </span>
      )}
    </Link>
  );
}

/** Nav section with uppercase label header */
function NavSection({
  section,
  location,
}: {
  section: NavSection;
  location: ReturnType<typeof useLocation>;
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-5">
      <p
        className="text-[10px] font-bold uppercase tracking-[0.16em] mb-2 px-3 whitespace-nowrap"
        style={{ color: "var(--sidebar-text-muted)" }}
      >
        {t(section.label)}
      </p>
      <nav className="flex flex-col gap-0.5 px-1">
        {section.items.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (!item.exact &&
             item.href !== "/" &&
             location.pathname.startsWith(item.href) &&
             location.pathname.charAt(item.href.length) === "/");
          return <NavRow key={item.href} item={item} isActive={isActive} />;
        })}
      </nav>
    </div>
  );
}

/** User info row with avatar + name + email + dropdown */
function UserRow({
  userName,
  userEmail,
  userPhotoUrl,
  onSignOut,
  isAdmin = false,
}: {
  userName: string;
  userEmail: string;
  userPhotoUrl?: string | null;
  onSignOut: () => void;
  isAdmin?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-xl transition-colors cursor-pointer",
            "text-[var(--sidebar-text)] hover:text-[var(--sidebar-text)] hover:bg-[var(--sidebar-active-bg)]",
            "w-full",
          )}
        >
          <UserAvatar name={userName} photoUrl={userPhotoUrl} sizeClass="size-6" textClass="text-[10px]" />
          <span className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
            <span className="text-sm font-medium truncate">{userName}</span>
            <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60 rtl:scale-x-100" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={getEffectiveDir() === "rtl" ? "end" : "start"}
        className="min-w-[14rem] rounded-xl p-1.5"
        sideOffset={4}
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-warm)",
        }}
      >
        <div
          className={cn(
            "flex items-center gap-2.5 px-3 py-2.5 rounded-lg mb-1",
            getEffectiveDir() === "rtl" ? "flex-row-reverse" : "flex-row",
          )}
          style={{ background: "var(--sidebar-active-bg)" }}
        >
          <UserAvatar name={userName} photoUrl={userPhotoUrl} sizeClass="size-8" editable />
          <div className={cn("flex flex-col min-w-0", getEffectiveDir() === "rtl" ? "text-end" : "text-start")}>
            <span className="text-sm font-semibold truncate capitalize">
              {userName}
            </span>
            <span
              className="text-[10px] truncate"
              style={{ color: "var(--sidebar-text-muted)" }}
            >
              {userEmail}
            </span>
          </div>
        </div>
        <DropdownMenuItem
          asChild
          className={cn(
            "rounded-lg cursor-pointer transition-colors",
            "focus:bg-[var(--sidebar-active-bg)] focus:text-[var(--sidebar-text)]",
            "text-[var(--sidebar-text-muted)]",
          )}
          style={{ padding: "0.5rem 0.75rem" }}
        >
          <Link
            to={isAdmin ? "/admin/profile" : "/settings/profile"}
            className={cn(
              "flex items-center gap-2.5",
              getEffectiveDir() === "rtl" ? "flex-row-reverse" : "flex-row",
            )}
          >
            <UserCircle className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium">{t("sidebar.profile")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className={cn(
            "rounded-lg cursor-pointer transition-colors",
            "focus:bg-[var(--sidebar-active-bg)] focus:text-[var(--sidebar-text)]",
            "text-[var(--sidebar-text-muted)]",
          )}
          style={{ padding: "0.5rem 0.75rem" }}
        >
          <Link
            to={isAdmin ? "/admin/settings" : "/dashboard/settings"}
            className={cn(
              "flex items-center gap-2.5",
              getEffectiveDir() === "rtl" ? "flex-row-reverse" : "flex-row",
            )}
          >
            <Settings className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium">{t("sidebar.settings")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator
          className="my-1.5"
          style={{ background: "var(--sidebar-border)" }}
        />
        <DropdownMenuItem
          onClick={onSignOut}
          className={cn(
            "rounded-lg cursor-pointer transition-colors",
            "flex items-center gap-2.5",
            getEffectiveDir() === "rtl" ? "flex-row-reverse" : "flex-row",
          )}
          style={{ padding: "0.5rem 0.75rem", backgroundColor: "transparent", transition: "background-color 150ms" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "oklch(from var(--sidebar-danger) l c h / 0.15)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
          <LogOut className="w-4 h-4 shrink-0" style={{ color: "var(--sidebar-danger)" }} />
          <span className="text-sm font-medium" style={{ color: "var(--sidebar-danger)" }}>{t("sidebar.signOut")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ── Main component ─────────────────────────────────────────────────────────── */

export function Sidebar({
  userName,
  userEmail,
  userPhotoUrl,
  navSections,
  onSignOut,
  showBell = false,
  unreadNotificationCount = 0,
  isAdmin = false,
}: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);
  const allNotificationsPath = isAdmin ? "/admin/notifications" : "/notifications";

  return (
    <aside
      className={cn(
        "shrink-0 flex flex-col",
        "border border-[var(--border)]",
        "rounded-[1.25rem]",
        "mt-[0.75rem] h-[calc(100vh-1.5rem)]",
        "ms-[0.75rem]",
      )}
      style={{
        width: SIDEBAR_WIDTH,
        background: 'var(--card)',
        boxShadow: '0 2px 20px rgba(26,25,23,0.06)',
      }}
    >
      {/* ── Brand header ── */}
      <div className="flex items-center gap-2 px-3 pt-4 pb-3">
        <div className="flex-1 min-w-0 overflow-hidden">
          <Link
            to="/"
            className="inline-block"
            aria-label={t("sidebar.home", { defaultValue: "Home" })}
          >
            <Logo height={48} />
          </Link>
        </div>
        {/* Bell — notification center popover */}
        {showBell ? (
          <Popover open={bellOpen} onOpenChange={setBellOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative w-9 h-9 flex items-center justify-center rounded-xl transition-colors cursor-pointer hover:bg-[var(--sidebar-active-bg)]"
                style={{ color: "var(--sidebar-text-muted)" }}
                aria-label={t("sidebar.notifications")}
              >
                <Bell className="w-4 h-4" strokeWidth={1.75} />
                {unreadNotificationCount > 0 && (
                  <span
                    className="absolute top-1 end-1 w-2 h-2 rounded-full"
                    style={{ background: "var(--sidebar-danger)" }}
                    aria-hidden
                  />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="center"
              sideOffset={8}
              className="p-0 overflow-hidden"
              style={{ width: 360 }}
            >
              <NotificationCenter onNavigate={() => { setBellOpen(false); navigate(allNotificationsPath); }} />
            </PopoverContent>
          </Popover>
        ) : (
          <button
            type="button"
            className="relative w-9 h-9 flex items-center justify-center rounded-xl transition-colors cursor-pointer hover:bg-[var(--sidebar-active-bg)]"
            style={{ color: "var(--sidebar-text-muted)" }}
            aria-label={t("sidebar.notifications")}
          >
            <Bell className="w-4 h-4" strokeWidth={1.75} />
            <span
              className="absolute top-1.5 end-1.5 w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--sidebar-text-active)" }}
            />
          </button>
        )}
      </div>

      {/* ── Nav sections (dynamic) ── */}
      <div className="flex-1 overflow-y-auto px-1">
        {navSections.map((section) => (
          <NavSection
            key={section.label}
            section={section}
            location={location}
          />
        ))}
      </div>

      {/* ── Bottom: Account row ── */}
      <div
        className="shrink-0 flex flex-col gap-1 px-1 pb-3 pt-1"
        style={{ borderTop: "1px solid var(--sidebar-border)" }}
      >
        {/* Account row */}
        <UserRow
          userName={userName}
          userEmail={userEmail}
          userPhotoUrl={userPhotoUrl}
          onSignOut={onSignOut}
          isAdmin={isAdmin}
        />
      </div>
    </aside>
  );
}
